import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Ship, Waves, Calendar, Clock, Users, AlertCircle, Shield, Star, Anchor, Wind, Sun } from 'lucide-react';
import NavigationHeader from '../homepage/components/NavigationHeader';
import FooterSection from '../homepage/components/FooterSection';

export default function TourismActivitiesHub() {
  const [selectedActivity, setSelectedActivity] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');

  const featuredActivities = [
  {
    id: 1,
    title: "Paddle Boat Adventure",
    category: "water",
    icon: Waves,
    image: "https://images.unsplash.com/photo-1685101175807-f27bae0d9dab",
    alt: "Colorful paddle boats lined up at wooden dock on calm turquoise water, tropical forest in background, recreational water activity equipment ready for tourists",
    duration: "1-2 hours",
    difficulty: "Easy",
    capacity: "2-4 persons",
    price: "₱500/hour",
    description: "Self-guided paddle boat exploration of the protected river estuaries, offering intimate wildlife viewing opportunities in shallow water zones.",
    highlights: ["Mangrove exploration", "Bird watching", "Photography-friendly", "Family-suitable"],
    safetyFeatures: ["Life jackets provided", "Shallow water routes", "Weather monitoring", "Guide assistance available"]
  },
  {
    id: 2,
    title: "River Cruise Eco-Tour",
    category: "water",
    icon: Ship,
    image: "https://images.unsplash.com/photo-1653188167325-f90d3cf7ee0b",
    alt: "Traditional wooden boat gliding through emerald green river surrounded by dense tropical rainforest, tourists aboard enjoying guided nature tour with local naturalist",
    duration: "3-4 hours",
    difficulty: "Easy",
    capacity: "15-30 persons",
    price: "₱1,200/person",
    description: "Guided river cruise with expert naturalists showcasing aquatic ecosystems, underground river formations, and wildlife conservation zones.",
    highlights: ["Underground river access", "Wildlife spotting", "Educational commentary", "Conservation insights"],
    safetyFeatures: ["Licensed operators", "Safety briefing", "Emergency equipment", "Weather contingency plans"]
  },
  {
    id: 3,
    title: "Sabang, Panaguman & Marta Fe Beaches Paradise",
    category: "water",
    icon: Sun,
    image: "https://images.unsplash.com/photo-1619341374997-13a03b09cf07",
    alt: "Stunning white sand beach with crystal-clear turquoise waters lapping at pristine shore, dramatic limestone karst formations and cliffs rising majestically from the sea, showcasing the breathtaking coastal paradise where PPSRNP's marine and terrestrial ecosystems converge in perfect harmony",
    duration: "Half day or full day",
    difficulty: "Easy",
    capacity: "Beach access varies",
    price: "₱800/person (includes transport)",
    description: "Discover PPSRNP's breathtaking coastal paradise at Sabang, Panaguman, and Marta Fe Beaches—where pristine white sand meets crystal-clear turquoise waters beneath dramatic limestone karst formations rising majestically from the sea. These protected beaches represent the spectacular convergence of marine and terrestrial ecosystems, offering visitors a chance to experience the 'mountain-to-sea' conservation approach that makes PPSRNP unique. Relax on powdery sand, snorkel in protected coral gardens, or simply marvel at the geological artistry of ancient limestone cliffs—all while supporting conservation through responsible eco-tourism.",
    highlights: ["Swimming and snorkeling", "Limestone cliff scenery", "Marine biodiversity", "Photography paradise"],
    safetyFeatures: ["Lifeguard presence", "Designated swimming areas", "First aid stations", "Weather advisories"]
  },
  {
    id: 4,
    title: "Wildlife Watching Expedition",
    category: "adventure",
    icon: Users,
    image: "/assets/images/Wildlife_Watching-1769667561354.jpg",
    alt: "Blue-headed Racket-tail parrot (Prioniturus platenae) photographed by Erickson A. Tabayag in 2014, showcasing the endemic species with iridescent blue-green plumage and distinctive crest in PPSRNP pristine forest habitat",
    duration: "3-4 hours",
    difficulty: "Moderate",
    capacity: "6-10 persons",
    price: "₱1,500/person",
    description: "Guided wildlife observation trek through PPSRNP's pristine forest habitats, offering rare opportunities to observe endemic mammals like the Palawan bearded pig, forest birds, reptiles, and other creatures found nowhere else on Earth. Expert naturalist guides share insights about animal behavior, ecological roles, and conservation efforts while maintaining respectful observation distances that prioritize wildlife welfare.",
    highlights: ["Endemic mammal sightings", "Expert naturalist guides", "Photography opportunities", "Conservation education"],
    safetyFeatures: ["Experienced wildlife guides", "Safe observation distances", "Emergency communication", "First aid equipped"]
  },
  {
    id: 5,
    title: "Sunset River Safari",
    category: "water",
    icon: Sun,
    image: "https://images.unsplash.com/photo-1671590710136-742bc874b374",
    alt: "Boat silhouetted against orange and pink sunset sky on calm river, golden hour lighting reflecting on water surface, peaceful evening river journey atmosphere",
    duration: "2-3 hours",
    difficulty: "Easy",
    capacity: "10-20 persons",
    price: "₱1,500/person",
    description: "Evening cruise capturing spectacular sunset views while observing nocturnal wildlife emerging for feeding time along the riverbanks.",
    highlights: ["Golden hour photography", "Nocturnal wildlife", "Local snacks included", "Romantic atmosphere"],
    safetyFeatures: ["LED navigation lights", "Experienced crew", "Emergency protocols", "First aid equipped"]
  },
  {
    id: 6,
    title: "Kayak Expedition",
    category: "adventure",
    icon: Anchor,
    image: "https://images.unsplash.com/photo-1594470387821-ab5852cffac6",
    alt: "Yellow kayak navigating through narrow river channel surrounded by lush green vegetation, adventure tourism activity in pristine natural waterway",
    duration: "2-3 hours",
    difficulty: "Moderate",
    capacity: "1-2 persons",
    price: "₱800/person",
    description: "Active kayaking adventure through protected waterways with opportunities to explore hidden coves and pristine mangrove forests.",
    highlights: ["Close-to-nature experience", "Physical activity", "Hidden waterway access", "Small group experience"],
    safetyFeatures: ["Kayak training provided", "Safety equipment", "Guide accompaniment", "Emergency communication"]
  },
  {
    id: 7,
    title: "Underground River Expedition",
    category: "adventure",
    icon: Ship,
    image: "/assets/images/UNDERGROUND_-_6-1769151502955.jpg",
    alt: "Tourists in orange life vests approaching the legendary underground river entrance through crystal-clear emerald waters, surrounded by ancient limestone cliffs and pristine rainforest",
    duration: "4-5 hours",
    difficulty: "Moderate",
    capacity: "6-8 persons per boat",
    price: "₱1,800/person",
    description: "Experience the gateway to wonder as your boat glides across emerald waters toward the world-famous underground river entrance. This UNESCO World Heritage adventure combines scenic approach journey with the legendary cave exploration.",
    highlights: ["Cave entrance approach", "Professional guides", "Safety equipment included", "Photo opportunities"],
    safetyFeatures: ["Life vests mandatory", "Experienced local guides", "Weather monitoring", "Emergency protocols"]
  },
  {
    id: 8,
    title: "Pristine Beach Paradise Experience",
    category: "water",
    icon: Sun,
    image: "https://images.unsplash.com/photo-1619341374997-13a03b09cf07",
    alt: "Stunning white sand beach with crystal-clear turquoise waters lapping at shore, dramatic limestone karst formations rising from sea, pristine coastal paradise showcasing PPSRNP's marine and terrestrial ecosystem convergence",
    duration: "Half day or full day",
    difficulty: "Easy",
    capacity: "Beach access varies",
    price: "₱800/person (includes transport)",
    description: "Discover PPSRNP's breathtaking coastal paradise where pristine white sand beaches meet crystal-clear turquoise waters beneath dramatic limestone karst formations. These protected beaches represent the spectacular convergence of marine and terrestrial ecosystems, offering visitors a chance to experience the 'mountain-to-sea' conservation approach that makes PPSRNP unique. Relax on powdery sand, snorkel in protected coral gardens, or simply marvel at the geological artistry of ancient limestone cliffs rising directly from the sea—all while supporting conservation through responsible eco-tourism.",
    highlights: ["Swimming and snorkeling", "Limestone cliff scenery", "Marine biodiversity", "Photography paradise"],
    safetyFeatures: ["Lifeguard presence", "Designated swimming areas", "First aid stations", "Weather advisories"]
  },
  {
    id: 9,
    title: "Community River Boat Tour",
    category: "water",
    icon: Ship,
    image: "https://images.unsplash.com/photo-1702477347184-5f36f44de2a4",
    alt: "Traditional wooden boats operated by local community members navigating through pristine mangrove-lined river channels, showcasing sustainable community-based tourism and conservation partnership",
    duration: "2-3 hours",
    difficulty: "Easy",
    capacity: "8-12 persons per boat",
    price: "₱900/person",
    description: "Experience authentic community-based eco-tourism aboard traditional wooden boats operated by local river communities who have navigated these waters for generations. This intimate mangrove tour showcases the successful partnership between PPSRNP conservation management and local communities, where sustainable tourism creates economic opportunities while protecting critical river ecosystems. Your skilled local guide—often a third-generation boat operator—shares traditional ecological knowledge about mangrove forests, tidal patterns, and the diverse wildlife that depends on these productive estuarine habitats. Every tour directly supports local families while funding mangrove restoration and river conservation programs.",
    highlights: ["Community-operated tours", "Mangrove ecosystem education", "Traditional boat experience", "Direct community benefit"],
    safetyFeatures: ["Experienced local operators", "Traditional boat safety standards", "Life jackets provided", "Weather-aware scheduling"]
  }];


  const bookingRequirements = [
  { icon: Calendar, text: "Book 24-48 hours in advance for guaranteed availability" },
  { icon: Shield, text: "Valid ID required for all participants" },
  { icon: Users, text: "Minimum age: 6 years (with adult supervision)" },
  { icon: AlertCircle, text: "Weather-dependent - flexible rescheduling available" }];


  const conservationGuidelines = [
  "Maintain safe distance from wildlife",
  "No littering - carry out all waste",
  "Follow designated water routes only",
  "Respect quiet zones for nesting areas",
  "Photography without flash only",
  "No feeding of wild animals"];


  const filteredActivities = selectedActivity === 'all' ?
  featuredActivities :
  featuredActivities?.filter((a) => a?.category === selectedActivity);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800">
      <NavigationHeader />
      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden mt-20">
        <img
          src="/assets/images/UNDERGROUND_-_6-1769151502955.jpg"
          alt="Tourists in orange life vests approaching the iconic underground river entrance through emerald-green waters surrounded by pristine tropical forest, capturing the anticipation and excitement of the PPSRNP adventure"
          className="w-full h-full object-cover" />

        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
          <div className="max-w-7xl mx-auto px-4 h-full flex flex-col justify-end pb-16">
            <Link to="/site-showcase-gallery" className="flex items-center space-x-2 text-white/90 hover:text-white mb-4 w-fit">
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Site Showcase</span>
            </Link>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Tourism Activities
            </h1>
            <p className="text-xl text-white/90 max-w-3xl">
              Immersive eco-tourism experiences featuring paddle boats, river cruises, and sustainable adventure activities
            </p>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Activity Filter */}
        <div className="flex justify-center space-x-4 mb-12">
          {['all', 'water', 'adventure']?.map((category) =>
          <button
            key={category}
            onClick={() => setSelectedActivity(category)}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
            selectedActivity === category ?
            'bg-cyan-600 text-white shadow-lg' :
            'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-cyan-50 dark:hover:bg-gray-600'}`
            }>

              {category === 'all' ? 'All Activities' : category === 'water' ? 'Water Tours' : 'Adventure'}
            </button>
          )}
        </div>

        {/* Featured Activities */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {filteredActivities?.map((activity) =>
          <div key={activity?.id} className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all">
              <div className="relative h-64 overflow-hidden group">
                <img
                src={activity?.image}
                alt={activity?.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute top-4 right-4 bg-cyan-600 text-white p-3 rounded-full">
                  <activity.icon className="h-6 w-6" />
                </div>
                <div className="absolute bottom-4 left-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg px-4 py-2">
                  <span className="text-2xl font-bold text-cyan-600">{activity?.price}</span>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  {activity?.title}
                </h3>
                <p className="text-gray-800 dark:text-gray-200 mb-4 leading-relaxed">
                  {activity?.description}
                </p>

                {/* Activity Details */}
                <div className="grid grid-cols-3 gap-4 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="text-center">
                    <Clock className="h-5 w-5 text-cyan-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-800 dark:text-gray-200">{activity?.duration}</p>
                  </div>
                  <div className="text-center">
                    <Users className="h-5 w-5 text-cyan-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-800 dark:text-gray-200">{activity?.capacity}</p>
                  </div>
                  <div className="text-center">
                    <Star className="h-5 w-5 text-cyan-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-800 dark:text-gray-200">{activity?.difficulty}</p>
                  </div>
                </div>

                {/* Highlights */}
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Highlights:</h4>
                  <div className="flex flex-wrap gap-2">
                    {activity?.highlights?.map((highlight, index) =>
                  <span key={index} className="px-3 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded-full text-xs">
                        {highlight}
                      </span>
                  )}
                  </div>
                </div>

                {/* Safety Features */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                    <Shield className="h-4 w-4 mr-2 text-green-600" />
                    Safety Features:
                  </h4>
                  <ul className="space-y-1">
                    {activity?.safetyFeatures?.map((feature, index) =>
                  <li key={index} className="text-sm text-gray-800 dark:text-gray-200 flex items-start">
                        <span className="text-green-600 mr-2">✓</span>
                        {feature}
                      </li>
                  )}
                  </ul>
                </div>

                <button className="w-full mt-6 bg-cyan-600 text-white py-3 rounded-lg font-semibold hover:bg-cyan-700 transition-colors">
                  Book Now
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Booking Requirements */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl mb-16">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-8 text-center">
            Booking Requirements
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {bookingRequirements?.map((req, index) =>
            <div key={index} className="flex flex-col items-center text-center p-6 rounded-xl bg-cyan-50 dark:bg-gray-700">
                <div className="bg-cyan-600 text-white p-4 rounded-full mb-4">
                  <req.icon className="h-6 w-6" />
                </div>
                <p className="text-sm text-gray-800 dark:text-gray-200">{req?.text}</p>
              </div>
            )}
          </div>
        </div>

        {/* Conservation Guidelines */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 shadow-xl text-white">
          <div className="flex items-center justify-center space-x-3 mb-8">
            <Shield className="h-8 w-8" />
            <h2 className="text-3xl font-bold">Conservation Guidelines</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {conservationGuidelines?.map((guideline, index) =>
            <div key={index} className="flex items-start space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <Wind className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{guideline}</span>
              </div>
            )}
          </div>
          <p className="text-center mt-8 text-white/90">
            Your cooperation helps preserve this UNESCO World Heritage Site for future generations
          </p>
        </div>

        {/* Weather & Season Info */}
        <div className="mt-16 bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 text-center">
            Best Visiting Conditions
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 rounded-xl border-2 border-cyan-200 dark:border-cyan-800">
              <Sun className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">Dry Season</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">November - May: Best weather, calm waters</p>
            </div>
            <div className="text-center p-6 rounded-xl border-2 border-cyan-200 dark:border-cyan-800">
              <Waves className="h-12 w-12 text-cyan-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">Water Levels</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Monitored daily for optimal tour conditions</p>
            </div>
            <div className="text-center p-6 rounded-xl border-2 border-cyan-200 dark:border-cyan-800">
              <AlertCircle className="h-12 w-12 text-orange-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">Weather Alerts</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Real-time updates and safety notifications</p>
            </div>
          </div>
        </div>
      </div>
      <FooterSection />
    </div>
  );



}